import React from "react"
import { Link } from "react-router-dom"
import { Pencil, Trash2, UserX, MessageSquare, MessageCircle, Phone, Mail, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Can } from "src/components/shared/Can"
import { LeadStatusBadge } from "./LeadStatusBadge"
import { LeadSourceBadge } from "./LeadSourceBadge"

// Backend masks phone numbers for the caller role (see PhoneNumberMasker),
// which prefixes them with asterisks. A masked number is not dialable.
function isMasked(phone) {
  return typeof phone === "string" && phone.includes("*")
}

// Normalise an Indian mobile number to E.164 (+91…) for tel/sms/WhatsApp links.
// Returns null when there's nothing dialable (blank or masked).
function toE164(phone) {
  if (!phone || isMasked(phone)) return null
  const digits = String(phone).replace(/\D/g, "")
  if (digits.length === 10) return `+91${digits}`
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`
  return `+${digits}`
}

// ── Skeleton rows shown while loading ─────────────────────────────────────────
function SkeletonRow() {
  return (
    <TableRow>
      {[40, 180, 130, 110, 90, 150].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ hasFilters, onNewLead }) {
  return (
    <TableRow>
      <TableCell colSpan={6}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserX className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">No leads found</p>
          <p className="text-xs text-slate-400 mt-1">
            {hasFilters
              ? "Try adjusting your search or filters"
              : "Get started by adding your first lead"}
          </p>
          {!hasFilters && (
            <Button size="sm" className="mt-4" onClick={onNewLead}>
              + New Lead
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

// ── Main table ────────────────────────────────────────────────────────────────
export function LeadsTable({ leads = [], loading, meta, filters, page, onPageChange, onEdit, onDelete, onNewLead, onMessage }) {
  const hasFilters = !!(filters?.q || filters?.status || filters?.source || (filters?.scope && filters.scope !== "all"))

  const [sortField, setSortField] = React.useState(null)
  const [sortDirection, setSortDirection] = React.useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedLeads = React.useMemo(() => {
    if (!sortField || !sortDirection) return leads
    return [...leads].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.name || ""
        bVal = b.name || ""
      } else if (sortField === "assigned_to") {
        aVal = a.assigned_to?.full_name || ""
        bVal = b.assigned_to?.full_name || ""
      } else if (sortField === "source") {
        aVal = a.source || ""
        bVal = b.source || ""
      } else if (sortField === "status") {
        aVal = a.status || ""
        bVal = b.status || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [leads, sortField, sortDirection])

  const renderSortableHeader = (label, field) => {
    const isSorted = sortField === field
    return (
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className="inline-flex h-auto items-center gap-1 p-0 font-semibold text-black hover:bg-transparent hover:text-black"
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </Button>
    )
  }

  const startRow = ((page - 1) * (meta?.per_page || 20)) + 1
  const endRow = Math.min(startRow + (leads.length - 1), meta?.total || 0)

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="min-w-[200px] text-black">
              {renderSortableHeader("Lead Name", "name")}
            </TableHead>
            <TableHead className="w-[180px] text-center text-black">
              <div className="flex justify-center">
                {renderSortableHeader("Assigned to", "assigned_to")}
              </div>
            </TableHead>
            <TableHead className="w-[150px] text-center text-black">
              <div className="flex justify-center">
                {renderSortableHeader("Source", "source")}
              </div>
            </TableHead>
            <TableHead className="w-[140px] text-center text-black">
              <div className="flex justify-center">
                {renderSortableHeader("Status", "status")}
              </div>
            </TableHead>
            <TableHead className="w-[200px] text-center text-black">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : leads.length === 0 ? (
            <EmptyState hasFilters={hasFilters} onNewLead={onNewLead} />
          ) : (
            sortedLeads.map((lead, idx) => (
              <TableRow key={lead.id}>
                <TableCell className="text-xs text-slate-400">{startRow + idx}</TableCell>
                {/* Lead Name + phone */}
                <TableCell>
                  <Link to={`/leads/${lead.id}`} className="font-medium text-slate-900 text-[13px] hover:text-blue-600 hover:underline">{lead.name}</Link>
                  {lead.phone && (
                    <p
                      className={`text-[11px] text-slate-400 mt-0.5 ${isMasked(lead.phone) ? "select-none" : ""}`}
                      onCopy={isMasked(lead.phone) ? (e) => e.preventDefault() : undefined}
                    >
                      {lead.phone}
                    </p>
                  )}
                </TableCell>

                {/* Assigned to */}
                <TableCell className="text-[13px] text-center">
                  {lead.assigned_to
                    ? <span className="text-slate-700">{lead.assigned_to.full_name}</span>
                    : <span className="text-slate-400 text-xs">Unassigned</span>
                  }
                </TableCell>

                {/* Source */}
                <TableCell className="text-center">
                  <LeadSourceBadge source={lead.source} />
                </TableCell>

                {/* Status */}
                <TableCell className="text-center">
                  <LeadStatusBadge status={lead.status} />
                </TableCell>

                {/* Actions */}
                <TableCell className="text-center">
                  {(() => {
                    const tel = toE164(lead.phone)
                    const hasPhone = !!tel
                    const hasEmail = !!lead.email
                    return (
                      <div className="flex items-center justify-center gap-0.5">
                        <Can resource="leads" action="update">
                          <Button variant="ghost" size="icon" onClick={() => onEdit(lead)} title="Edit lead">
                            <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                          </Button>
                        </Can>

                        {/* Text (SMS) — opens the template messaging drawer */}
                        <Can resource="templates" action="send">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => onMessage(lead, "sms")}
                            disabled={!hasPhone}
                            title={hasPhone ? `Text ${lead.phone}` : "No phone number"}
                          >
                            <MessageSquare className={`h-3.5 w-3.5 ${hasPhone ? "text-slate-400 hover:text-blue-600" : "text-slate-300"}`} />
                          </Button>
                        </Can>

                        {/* WhatsApp — opens the template messaging drawer */}
                        <Can resource="templates" action="send">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => onMessage(lead, "whatsapp")}
                            disabled={!hasPhone}
                            title={hasPhone ? `WhatsApp ${lead.phone}` : "No phone number"}
                          >
                            <MessageCircle className={`h-3.5 w-3.5 ${hasPhone ? "text-slate-400 hover:text-green-600" : "text-slate-300"}`} />
                          </Button>
                        </Can>

                        {/* Email — opens the template messaging drawer */}
                        <Can resource="templates" action="send">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => onMessage(lead, "email")}
                            disabled={!hasEmail}
                            title={hasEmail ? `Email ${lead.email}` : "No email address"}
                          >
                            <Mail className={`h-3.5 w-3.5 ${hasEmail ? "text-slate-400 hover:text-violet-600" : "text-slate-300"}`} />
                          </Button>
                        </Can>

                        {/* Call — native tel: link */}
                        {tel ? (
                          <Button variant="ghost" size="icon" asChild title={`Call ${lead.phone}`}>
                            <a href={`tel:${tel}`}>
                              <Phone className="h-3.5 w-3.5 text-slate-400 hover:text-emerald-600" />
                            </a>
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" disabled title="No phone number">
                            <Phone className="h-3.5 w-3.5 text-slate-300" />
                          </Button>
                        )}

                        <Can resource="leads" action="destroy">
                          <Button variant="ghost" size="icon" onClick={() => onDelete(lead)} title="Delete lead">
                            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                          </Button>
                        </Can>
                      </div>
                    )
                  })()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Table footer — count + pagination */}
      {!loading && leads.length > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500">
            Showing {startRow}–{endRow} of {meta?.total ?? 0} leads
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              ← Prev
            </Button>
            <span className="px-3 text-xs text-slate-500">
              {page} / {meta?.total_pages ?? 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= (meta?.total_pages ?? 1)}
              onClick={() => onPageChange(page + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
